import React, { useCallback, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useScanGun from '../../hooks/useScanGun'
import { commitMove, defaultPositionFor, fetchItem } from '../../utils/warehouseApi'
import { signal } from '../../utils/feedback'
import { resolveScan } from './resolveScan'
import ScanPrompt from './ScanPrompt'
import PdaPhotos from './PdaPhotos'

/**
 * Scan the item, scan the tote it is in, scan the tote it is going to.
 *
 * Scanning the FROM tote looks redundant — Prism already knows where the item
 * is — but it is the only moment anyone physically verifies that claim. When
 * the scan disagrees with the record we move the item anyway, because the
 * operator holding it is right and the database is wrong, and send
 * `scanned_current_matches_system: false` so the backend records an
 * inventory_correction against the move. Consolidating a shelf therefore
 * audits it at the same time. None of that is the operator's problem, so the
 * disagreement is not announced mid-flow; the move is what they are told
 * about, once it has happened.
 *
 * The confirmation is evidence, not optimism: POST /move returns the committed
 * ItemLocationRead, so the slot shown on screen is the row Prism actually
 * stored, read back out of the same write. A second lookup runs alongside it
 * as a cross-check, but it can only ever add a note — it must never replace
 * the confirmation, because a search that lags or fails would then report
 * completed work as lost.
 *
 * The destination stays put between items: consolidating twenty things into
 * one tote costs two scans each rather than three.
 */
const REASON = 'consolidation'

const STEP_PROMPT = {
    item: 'Scan the item',
    from: 'Scan the tote it is in now',
    to: 'Scan the tote it is going to'
}

export default function PdaConsolidate() {
    const handedOver = useLocation().state?.item ?? null
    const [item, setItem] = useState(handedOver)
    const [from, setFrom] = useState(null)
    const [destination, setDestination] = useState(null)
    const [banner, setBanner] = useState(null)
    const [busy, setBusy] = useState(false)

    const step = !item ? 'item' : !from ? 'from' : 'to'

    const move = useCallback(async (subject, origin, target) => {
        setBusy(true)
        try {
            const moved = await commitMove({
                itemId: subject.item_id,
                fromShelf: origin.shelf_id,
                fromPosition: subject.current_position,
                toShelf: target.shelf_id,
                toPosition: defaultPositionFor(target.shelf_type ?? target.type),
                reason: REASON,
                scannedCurrentMatchesSystem: origin.shelf_id === subject.current_shelf_id
            })

            const slot = moved.position ? ` slot ${moved.position}` : ''
            const where = `${moved.shelf_id}${slot}`
            const isNew = target.is_new ? ' (new location)' : ''
            setBanner({ kind: 'success', text: `Moved to ${where}${isNew} · ${subject.title}` })
            signal('done')

            // Destination is sticky; the item and its origin are not.
            setItem(null)
            setFrom(null)

            // Cross-check after the fact. Only ever downgrades the wording, and
            // only when Prism positively disagrees.
            confirmLanded(subject.item_id, moved.shelf_id).then((landed) => {
                if (landed === false) {
                    setBanner({
                        kind: 'warn',
                        text: `Moved to ${where}, but Prism is still reporting ${subject.title} elsewhere. Worth a look.`
                    })
                }
            })
            return
        } catch (error) {
            setBanner({ kind: 'error', text: error.message || 'Could not move that.' })
            signal('problem')
        } finally {
            setBusy(false)
        }
    }, [])

    const onScan = useCallback(async (raw) => {
        if (busy) {
            signal('problem')
            return
        }
        setBanner(null)
        const result = await resolveScan(raw)

        if (result.kind === 'error') {
            setBanner({ kind: 'error', text: result.message })
            signal('problem')
            return
        }
        if (result.kind === 'item') {
            if (!result.item.current_shelf_id) {
                setBanner({ kind: 'warn', text: 'That item has no location yet. Use Putaway.' })
                signal('problem')
                return
            }
            setItem(result.item)
            setFrom(null)
            signal('ok')
            return
        }

        if (step === 'item') {
            setBanner({ kind: 'warn', text: 'Scan the item first.' })
            signal('problem')
            return
        }
        if (step === 'from') {
            setFrom(result.location)
            signal('ok')
            if (destination) move(item, result.location, destination)
            return
        }
        setDestination(result.location)
        move(item, from, result.location)
    }, [busy, step, item, from, destination, move])

    // Always listening; see PdaLookup for why gating on `busy` swallowed scans.
    useScanGun(onScan)

    return (
        <>
            <ScanPrompt
                prompt={STEP_PROMPT[step]}
                hint={destination ? `Everything goes to ${destination.shelf_id}.` : 'The destination sticks for the next item.'}
                placeholder={step === 'item' ? 'Inventory lookup' : 'Locator code'}
                onSubmit={onScan}
                disabled={busy}
            />

            {banner && <div className={`pda-banner is-${banner.kind}`}>{banner.text}</div>}

            {item && (
                <div className="pda-held">
                    <div className="pda-held-title">{item.title}</div>
                    <div className="pda-code">{item.item_id}</div>
                    <div className="pda-location">{from ? from.shelf_id : item.current_shelf_id}</div>
                    <PdaPhotos item={item} />
                </div>
            )}

            {destination && (
                <>
                    <div className="pda-sub">Destination</div>
                    <div className="pda-location">{destination.shelf_id}</div>
                    <button className="pda-btn" onClick={() => setDestination(null)} disabled={busy}>
                        Change destination
                    </button>
                </>
            )}
        </>
    )
}

/**
 * Read the item back and check Prism agrees it is where we put it.
 *
 * Returns true when confirmed, false when Prism reports it somewhere else, and
 * null when the check itself could not be made. A failed check is not a failed
 * move: the write already returned successfully, so an unreachable read must
 * not tell the operator their work was lost.
 */
async function confirmLanded(itemId, shelfId) {
    try {
        const item = await fetchItem(itemId)
        if (!item) return null
        return item.current_shelf_id === shelfId
    } catch {
        return null
    }
}
